import React, { useContext, useMemo } from 'react'
import { WithBorder, SignUpFormMask as SignUpFormMaskBase } from '@grammarly/identity-ui'

import { getConfig } from '../../config'
import { ContentProps } from '../renderer'
import { renderRichTextSingleLineContent } from '../renderer/rich-text'
import { SessionStorageKey, setSessionStorageItem } from '../../lib/utils/session-storage'
import { navHelper } from '../../lib/services/navigation'
import { SignUpFormMaskType, TypeComponentSignUpFormMask } from '../../lib/types/component-sign-up-form-mask'
import { UnifiedAuthTracker } from '../unified-auth/tracking'
import { useApiService, useExperimentService } from '../../lib/hooks'
import { PageContext } from '../../contexts/page-context'
import { composeUrl } from '../../lib/utils/url'

import '@grammarly/design-system/dist/index.css'

const { signup, signin } = getConfig().paths.funnel

const getTargetDestinationQueryParams = (type: SignUpFormMaskType) => {
  switch (type) {
    case SignUpFormMaskType.Edu:
      return {
        pageStyle: 'edu',
        from: 'edu',
      }
    case SignUpFormMaskType.Generic:
    default:
      return {}
  }
}

export const SignUpFormMaskComponent: React.FC<ContentProps<TypeComponentSignUpFormMask>> = ({ structure }) => {
  const { auth } = useApiService()
  const { isGDPR } = useExperimentService()
  const { pageId, slug } = useContext(PageContext)

  const unifiedAuthTracker: UnifiedAuthTracker = useMemo(
    () => new UnifiedAuthTracker(pageId, slug, isGDPR),
    [pageId, slug, isGDPR]
  )

  const { type, heading, emailPlaceholder } = structure.fields
  const { title, description } = heading.fields

  const targetDestinationQueryParams = getTargetDestinationQueryParams(type)

  return (
    <WithBorder>
      <SignUpFormMaskBase
        api={auth}
        tracker={unifiedAuthTracker}
        title={title && renderRichTextSingleLineContent(title)}
        subtitle={description && renderRichTextSingleLineContent(description)}
        titleLinkHref={composeUrl(signin, {
          ...navHelper.currentQueryParams,
          ...targetDestinationQueryParams,
        })}
        emailInputLabel={emailPlaceholder}
        onFormSubmitSuccess={(authInfo) => {
          setSessionStorageItem(SessionStorageKey.loginHint, authInfo.email)
          setSessionStorageItem(SessionStorageKey.loginHintType, authInfo.loginType)

          if (authInfo.accountExists) {
            navHelper.navigateFunnelPath(signin, {
              ...navHelper.currentQueryParams,
              ...targetDestinationQueryParams,
              skip_select_account: 1, // override redirect to /signin/select-account
            })
          } else {
            navHelper.navigateFunnelPath(signup, {
              ...navHelper.currentQueryParams,
              ...targetDestinationQueryParams,
            })
          }
        }}
      />
    </WithBorder>
  )
}
